import type { SpecDefinition } from '../../api/types';
import { DateInput, Field, Input, Select } from '../ui';

export type SpecFormValue = string; // Formularwert immer als String; Umwandlung beim Speichern

export const specToFormValue = (v: string | number | boolean | null | undefined): SpecFormValue => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

/** Formularwert → API-Wert; '' → null. Zahl mit Komma wird akzeptiert. */
export const formValueToSpec = (def: SpecDefinition, v: SpecFormValue): string | number | boolean | null => {
  const t = v.trim();
  if (t === '') return null;
  switch (def.data_type) {
    case 'integer':
    case 'decimal': {
      const n = Number(t.replace(',', '.'));
      return Number.isFinite(n) ? n : t; // ungültig → Backend meldet Fehler am Feld
    }
    case 'boolean':
      return t === 'true';
    default:
      return t;
  }
};

/** Felder, die nur unter einer Bedingung abgefragt werden – z. B. Intel AMT nur bei Intel-CPU. */
export const specVisible = (key: string, values: Record<string, string>): boolean => {
  if (key === 'intel_amt') return values.cpu_vendor === 'Intel' || /intel/i.test(values.cpu_model ?? '');
  return true;
};

/** Ja/Nein-Felder, die als Häkchen statt als Auswahl erscheinen. */
const CHECKBOX_SPECS = new Set(['intel_amt']);

export function SpecInput({ def, value, onChange, error }: { def: SpecDefinition; value: SpecFormValue; onChange: (v: SpecFormValue) => void; error?: string }) {
  const label = def.unit ? `${def.label} (${def.unit})` : def.label;
  return (
    <Field label={label} required={def.is_required} error={error} hint={def.description ?? undefined}>
      {(p) => {
        if (def.data_type === 'boolean' && CHECKBOX_SPECS.has(def.key)) {
          return (
            <label className="flex h-9 items-center gap-2 text-sm text-slate-700">
              <input
                {...p}
                type="checkbox"
                className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={value === 'true'}
                onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
              />
              Ja
            </label>
          );
        }
        switch (def.data_type) {
          case 'boolean':
            return (
              <Select {...p} value={value} onChange={(e) => onChange(e.target.value)}>
                <option value="">–</option>
                <option value="true">Ja</option>
                <option value="false">Nein</option>
              </Select>
            );
          case 'enum':
            return (
              <Select {...p} value={value} onChange={(e) => onChange(e.target.value)}>
                <option value="">–</option>
                {(def.allowed_values ?? []).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Select>
            );
          case 'date':
            return <DateInput {...p} value={value} onChange={(e) => onChange(e.target.value)} />;
          case 'integer':
          case 'decimal':
            return <Input {...p} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} />;
          default:
            return <Input {...p} value={value} onChange={(e) => onChange(e.target.value)} />;
        }
      }}
    </Field>
  );
}

export const formatSpecValue = (s: { data_type: string; value: string | number | boolean; unit: string | null }): string => {
  let v: string;
  if (typeof s.value === 'boolean') v = s.value ? 'Ja' : 'Nein';
  else if (typeof s.value === 'number') v = s.value.toLocaleString('de-DE');
  else if (s.data_type === 'date') v = s.value.split('-').reverse().join('.');
  else v = s.value;
  return s.unit ? `${v} ${s.unit}` : v;
};
